<template>
    <grid-area area="rail">
        <navigation-rail :variant="navigationRailVariant">
            <template #header>
                <flex-container :padding="Size.sm" justify-content="center">
                    <div :class="iconWrapperClasses">
                        <logo-icon v-if="!hasHeylohaBranding()" />
                        <logo-heyloha-icon v-if="hasHeylohaBranding()" />
                    </div>
                </flex-container>
            </template>
            <template #default>
                <template v-if="false">
                    <navigation-rail-item
                        label="Flow Dashboard"
                        :icon="Grid2Icon"
                        :icon-size="iconSize"
                        :to="{ name: 'flow-dashboard' }"
                        :show-label="isMenuVariantDefault"
                        is-custom-icon
                    />
                    <navigation-rail-item
                        label="Contacten"
                        :icon="UserIcon"
                        :icon-size="iconSize"
                        :to="{ name: 'lead-list' }"
                        :show-label="isMenuVariantDefault"
                        is-custom-icon
                    />
                </template>
                <navigation-rail-item
                    label="Dashboard"
                    :icon="MicrochipAiIcon"
                    :icon-size="iconSize"
                    :to="{ name: 'assistent-dashboard' }"
                    :show-label="isMenuVariantDefault"
                    is-custom-icon
                    :tooltip="showTooltip"
                />
                <navigation-rail-item
                    label="Berichten"
                    :icon="MessagesIcon"
                    :icon-size="iconSize"
                    :to="{ name: 'assistent-inbox' }"
                    :show-label="isMenuVariantDefault"
                    is-custom-icon
                    :tooltip="showTooltip"
                />
                <navigation-rail-item
                    v-if="isCompanyManager"
                    label="Medewerkers"
                    :icon="hasHeylohaBranding() ? UsersIcon : UserShieldIcon"
                    :icon-size="iconSize"
                    :to="{ name: 'user-list' }"
                    :show-label="isMenuVariantDefault"
                    is-custom-icon
                    :tooltip="showTooltip"
                />
                <navigation-rail-item
                    v-if="isCompanyManager"
                    label="Vestigingen"
                    :icon="LocationDotIcon"
                    :icon-size="iconSize"
                    :to="{ name: 'team-list' }"
                    :show-label="isMenuVariantDefault"
                    is-custom-icon
                    :tooltip="showTooltip"
                />
                <template v-if="isSuperAdmin">
                    <navigation-rail-item
                        v-if="isCompanyManager"
                        label="Licentiebeheer"
                        :icon="PeopleRoofIcon"
                        :icon-size="iconSize"
                        :to="{ name: 'license' }"
                        :show-label="isMenuVariantDefault"
                        is-custom-icon
                        :tooltip="showTooltip"
                    />
                </template>
            </template>
            <template #footer>
                <flex-container
                    :padding="Size.md"
                >
                    <collapse-box-decorator
                        fill
                        trigger="click"
                        :horizontal-position="collapseHorizontalPosition"
                        vertical-position="top-out"
                    >
                        <template #default="{ bindings }">
                            <button-default v-bind="bindings" style="width: 100%;">
                                <template v-if="isMenuVariantDefault">
                                    <flex-container
                                        align-items="center"
                                        justify-content="space-between"
                                        no-wrap
                                        inline
                                    >
                                        <flex-container
                                            align-items="center"
                                            size="auto"
                                        >
                                            <user-avatar
                                                :placeholder="currentUser.name.abbreviation"
                                                :color="'primary'"
                                                style="border-radius: 10px;"
                                            />
                                            <text-decorator weight="medium">
                                                {{ currentUser.name.firstName }}
                                            </text-decorator>
                                        </flex-container>

                                        <svg-icon :icon="bindings['aria-expanded'] ? ArrowDropDownIcon : ArrowDropUpIcon" :size="IconSize.sm" />
                                    </flex-container>
                                </template>
                                <template v-else>
                                    <user-avatar
                                        :placeholder="currentUser.name.abbreviation"
                                        :color="'primary'"
                                        style="border-radius: 10px;"
                                    />
                                </template>
                            </button-default>
                        </template>
                        <template #content>
                            <collapse-option-container no-scroll>
                                <collapse-option
                                    v-if="currentUser.keyflowRole?.equals(KeyflowRole.COMPANY_MANAGER) && hasWinckBranding()"
                                    @click="subscriptionModal?.overlay.openOverlay()"
                                >
                                    <flex-container inline gap="sm">
                                        <svg-icon
                                            :icon="TagIcon"
                                            :size="IconSize.sm"
                                            is-custom-icon
                                        />
                                        <text-decorator weight="medium">
                                            Abonnement
                                        </text-decorator>
                                    </flex-container>
                                </collapse-option>
                                <collapse-option @click="$router.push({ name: 'logout' })">
                                    <flex-container inline gap="sm">
                                        <svg-icon
                                            :icon="PowerRoundedIcon"
                                            :size="IconSize.sm"
                                            :color="'red-500'"
                                        />
                                        <text-decorator weight="medium">
                                            Uitloggen
                                        </text-decorator>
                                    </flex-container>
                                </collapse-option>
                            </collapse-option-container>
                        </template>
                    </collapse-box-decorator>
                </flex-container>

                <subscriptions-modal ref="subscriptionModal" />
            </template>
        </navigation-rail>
    </grid-area>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { type ExposedOverlay, IconSize, Size } from '@keyvue/one-ui';
    import { KeyflowRole } from '@keyvue/one-api-client';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import ArrowDropUpIcon from '@keyvue/one-material-icons/icons/arrow_drop_up.svg?component';
    import ArrowDropDownIcon from '@keyvue/one-material-icons/icons/arrow_drop_down.svg?component';
    import Grid2Icon from '@keyvue/one-awesome-icons/icons/regular/grid-2.svg?component';
    import UserIcon from '@keyvue/one-awesome-icons/icons/regular/user.svg?component';
    import PowerRoundedIcon from '@keyvue/one-material-icons/icons/power_rounded.svg?component';
    import TagIcon from '@keyvue/one-awesome-icons/icons/regular/tag.svg?component';
    import LocationDotIcon from '@keyvue/one-awesome-icons/icons/solid/location-dot.svg?component';
    import UserShieldIcon from '@keyvue/one-awesome-icons/icons/regular/user-shield.svg?component';
    import UsersIcon from '@keyvue/one-awesome-icons/icons/solid/users.svg?component';
    import LogoIcon from '@/assets/icons/favicon-winck.svg?component';
    import LogoHeylohaIcon from '@/assets/icons/logo-heyloha-avatar.svg?component';
    import PeopleRoofIcon from '@keyvue/one-awesome-icons/icons/solid/people-roof.svg?component';
    import MessagesIcon from '@keyvue/one-awesome-icons/icons/solid/messages.svg?component';
    import MicrochipAiIcon from '@keyvue/one-awesome-icons/icons/regular/microchip-ai.svg?component';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';
    import SubscriptionsModal from '@/subscription/subscription/components/SubscriptionsModal.vue';

    const { currentUser } = useClient();
    const { hasWinckBranding, hasHeylohaBranding } = useAppBranding();
    const isCompanyManager = computed(() => currentUser.value.keyflowRole?.equals(KeyflowRole.COMPANY_MANAGER));
    const isSuperAdmin = computed(() => currentUser.value.superAdmin);

    // const showTooltip = computed(() => hasHeylohaBranding());
    const showTooltip = ref(true);
    const subscriptionModal = ref<{ overlay: ExposedOverlay }>();

    const isMenuVariantDefault = computed(() => {
        // return hasWinckBranding();
        return false;
    });

    const isMenuVariantCompact = computed(() => {
        // return hasHeylohaBranding();
        return true;
    });

    const iconSize = computed(() => {
        // return hasHeylohaBranding() ? IconSize.sm : IconSize.md;
        return IconSize.sm;
    });

    const navigationRailVariant = computed(() => {
        // return hasHeylohaBranding() ? 'icon-button' : 'drawer';
        return 'icon-button';
    });

    const collapseHorizontalPosition = computed(() => {
        // return hasHeylohaBranding() ? 'left' : 'center';
        return 'left';
    });

    const iconWrapperClasses = computed(() => ({
        'icon-wrapper': true,
        'icon-wrapper--compact': isMenuVariantCompact.value,
    }));
</script>

<style lang="scss">
.icon-wrapper {
    max-width: 250px;
    width: 100%;
    box-sizing: border-box;
}
</style>
